import { type PatientProfileFormValuesType } from '@/components/modal/patient-profile-form-modal/form/schemas';
import type { PatientProfileWithSessionCount } from '@heidi/patients/contracts';
import { atom } from 'jotai';

/**
 * ID of the patient currently shown in the drawer. When non-null, the drawer is open.
 * The drawer resolves the full patient from the `useQueryPatientProfiles` cache so
 * it re-renders automatically after inline-edit mutations invalidate the list query.
 */
export const selectedPatientIdAtom = atom<string | null>(null);

/**
 * Snapshot of the full patient row captured at click time (paginated list path only,
 * when `release-patients-server-pagination` is on). When non-null and matching
 * `selectedPatientIdAtom`, the drawer reads this directly and skips the unpaginated
 * GET /patient-profiles/ fetch. Cleared on drawer close so stale data never persists
 * across opens. All other entry points leave this null and fall back to the query.
 */
export const selectedPatientSnapshotAtom = atom<PatientProfileWithSessionCount | null>(
  null
);

/**
 * Stitching primitive for the patient surface funnel. A fresh UUID is generated
 * when a profile view (drawer or full page) opens and persists open-to-close. It
 * is propagated to every in-view analytics event (tab switch, session expand,
 * field edit, document upload) and to the `session_created` event when a session
 * is started from the surface, so the open -> session funnel can be stitched
 * per-view rather than by fragile time-correlation. Null while no view is open.
 */
export const patientViewIdAtom = atom<string | null>(null);

/**
 * Session that opened the drawer/page. When set, the drawer/page is rendered in
 * "session context" and exposes session-scoped actions (e.g. unlink this session
 * from the patient). Cleared when the drawer closes or navigation moves away.
 */
export const patientDrawerSessionContextAtom = atom<string | null>(null);

export type PatientDrawerTab = 'sessions' | 'documents' | 'profile';

/**
 * Which tab the drawer opens to. Defaults to `sessions`. Openers that need a
 * specific tab set this before/while setting `selectedPatientIdAtom` (e.g. the
 * auto-extract Edit pencil opens straight to `profile`/Details). Reset to the
 * default when the drawer closes so the next open starts on Sessions.
 */
export const patientDrawerInitialTabAtom = atom<PatientDrawerTab>('sessions');

/**
 * Extraction-review prefill carried from the auto-extract Edit pencil into the
 * drawer's Details tab (patient-list path only). When non-null, the Details tab
 * renders the one-click "review and save" form (extracted values prefilled and
 * highlighted) instead of the per-field save-on-blur sections. Reset to `null`
 * on drawer close (and after a successful review save).
 *
 * Deliberately separate from the legacy modal's `patientProfileFormPrefillAtom`
 * so the two auto-extract flows never cross-wire.
 */
export interface PatientDrawerExtractionPrefill {
  /** Extracted values keyed by `PatientProfileForm` field name. */
  values: Partial<PatientProfileFormValuesType>;
  /** Field names that were auto-extracted; drives the focus-ring highlight. */
  fields: string[];
}

export const patientDrawerExtractionPrefillAtom =
  atom<PatientDrawerExtractionPrefill | null>(null);

/**
 * Bridges the extraction-review form's save action up to the drawer footer, so
 * the Save button can sit next to Delete (always visible) instead of inside the
 * scrollable form. Published by `ExtractionReviewSection` while it is mounted
 * (Details tab, review mode) and read by the footer's Save button. `null`
 * whenever the review form is not on screen.
 */
export interface PatientDrawerExtractionSaveControls {
  save: () => void;
  /** Form is dirty and not mid-save, so there is something to commit. The save
   *  button disables on this, which also covers the in-flight state. */
  canSave: boolean;
}

export const patientDrawerExtractionSaveAtom =
  atom<PatientDrawerExtractionSaveControls | null>(null);
